import { useCallback, useEffect, useRef, useState } from "react";
import { fetchDashboard } from "../data/dashboardApi";
import { readCache, writeCache } from "../data/dashboardCache";
import { refreshMs, retryMs } from "../constants";

const empty = {
  teamTarget: {},
  bestSeller: {},
  deals: {},
  existantDeals: {},
  soldServices: [],
  yearRankList: [],
  rankList: [],
  amount: 0,
  scoredTowardsTarget: 0,
};

// Minimaal aantal gelukte endpoints voordat we het scherm tonen.
const minOk = 4;

// Zonder deze twee klopt de bovenste helft van het scherm niet.
const required = ["rankList", "teamTarget"];

// Haalt de cijfers van één maand op, elke refreshMs opnieuw en na een
// (deels) mislukte ronde na retryMs.
export function useDashboardData(month, year) {
  const [data, setData] = useState(empty);
  const [isReady, setIsReady] = useState(false);

  const aliveRef = useRef(true);
  const abortRef = useRef(null);
  const retryRef = useRef(null);

  // skipCache: voor de vaste rondes, anders komt steeds hetzelfde antwoord.
  const load = useCallback(async (skipCache = false) => {
    const cached = skipCache ? null : readCache(month, year);
    if (cached) {
      setData(cached);
      setIsReady(true);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { data: fresh, okCount, ok } = await fetchDashboard(
        month,
        year,
        controller.signal,
      );
      if (!aliveRef.current) return;

      const missing = required.filter((key) => !ok[key]);
      if (okCount < minOk || missing.length) {
        const reason = missing.length
          ? `${missing.join(" en ")} ontbreekt`
          : `maar ${okCount} van de cijfers opgehaald`;
        console.warn(`[dashboard] ${reason}, over ${retryMs / 1000}s opnieuw`);
        retryRef.current = setTimeout(() => load(true), retryMs);
        return;
      }

      writeCache(month, year, fresh);
      setData(fresh);
      setIsReady(true);
    } catch (error) {
      if (error.name === "AbortError" || error.name === "CanceledError") return;
      if (!aliveRef.current) return;
      console.error("[dashboard] ophalen mislukt:", error);
      retryRef.current = setTimeout(() => load(true), retryMs);
    }
  }, [month, year]);

  useEffect(() => {
    aliveRef.current = true;
    setIsReady(false);
    load();

    const refresh = setInterval(() => load(true), refreshMs);

    return () => {
      aliveRef.current = false;
      abortRef.current?.abort();
      clearTimeout(retryRef.current);
      clearInterval(refresh);
    };
  }, [load]);

  // refresh: meteen opnieuw ophalen zonder cache, bijvoorbeeld als de tv aangaat.
  const refresh = useCallback(() => load(true), [load]);

  return { data, isReady, refresh };
}
